import { useState, useRef, useEffect } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { getContrastColor } from '@/lib/colorContrast';
import type { Status } from '@/types';

type Props = {
  statuses: Status[];
  // El callback recibe 0 cuando el usuario elige "— Todos —" (solo aplica si
  // includeAllOption es true). El consumidor traduce 0 → null si lo necesita.
  value: number | null;
  onChange: (idEst: number) => void;
  placeholder?: string;
  includeAllOption?: boolean;
  className?: string;
  disabled?: boolean;
};

// Sentinel id para el hover de la opción "— Todos —" (no es un idEst real).
const ALL_OPTION_HOVER_ID = -1;

export function StatusSelect({
  statuses,
  value,
  onChange,
  placeholder = '— Seleccionar —',
  includeAllOption = false,
  className = '',
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = value !== null ? statuses.find(s => s.idEst === value) : null;

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const triggerStyle = selected?.colorHex
    ? {
        backgroundColor: selected.colorHex,
        color: getContrastColor(selected.colorHex),
      }
    : { backgroundColor: '#f5f5f4' };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-3 py-2 text-sm rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={triggerStyle}
      >
        <span>{selected?.estado || placeholder}</span>
        <CaretDown size={14} weight="bold" />
      </button>

      {open && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded shadow-lg border border-stone-200 bg-white py-1"
          role="listbox"
        >
          {includeAllOption && (
            <li
              role="option"
              aria-selected={value === null}
              onMouseEnter={() => setHoveredId(ALL_OPTION_HOVER_ID)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => { onChange(0); setOpen(false); }}
              style={{
                backgroundColor:
                  value === null
                    ? '#e7e5e4'
                    : hoveredId === ALL_OPTION_HOVER_ID
                      ? '#f5f5f4'
                      : '#fff',
                color: '#000',
              }}
              className="px-3 py-1.5 text-sm cursor-pointer transition-colors duration-200"
            >
              — Todos —
            </li>
          )}
          {statuses.map(s => {
            const isSelected = s.idEst === value;
            const isHovered = hoveredId === s.idEst;
            const optStyle = isSelected && s.colorHex
              ? {
                  backgroundColor: s.colorHex,
                  color: getContrastColor(s.colorHex),
                }
              : {
                  backgroundColor: isHovered ? '#f5f5f4' : '#fff',
                  color: '#000',
                  borderLeft: s.colorHex ? `4px solid ${s.colorHex}` : 'none',
                };
            return (
              <li
                key={s.idEst}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHoveredId(s.idEst)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => { onChange(s.idEst); setOpen(false); }}
                style={optStyle}
                className="px-3 py-1.5 text-sm cursor-pointer font-medium transition-colors duration-200"
              >
                {s.estado}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
